// Customizable Area Start
import React, { Component } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, EffectCoverflow } from "swiper/modules";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import "swiper/css";
import "swiper/css/pagination";
const BlockQuote = require("./BlockQuote.svg").default;

const TESTIMONIALS_SLIDER_BREAKPOINTS = {
  300: {
    slidesPerView: 1.2,
    spaceBetween: 10,
  },
  600: {
    slidesPerView: 1.6,
    spaceBetween: 20,
  },
  900: {
    slidesPerView: 2,
    spaceBetween: 30,
  },
  1240: {
    slidesPerView: 2.4,
    spaceBetween: 40,
  },
};

const TestimonialContentWrapper = styled(Box)({
  maxWidth: "350px",
  width: "100%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  gap: "6px",
  marginTop: "35px",
  "& h5,h6,p,img": {
    transition: "0.3s ease",
  },
  "& .testimonial_card": {
    top: "0%",
    left: "50%",
    transform: "translateX(-50%)",
    position: "absolute",
    zIndex: -1,
    width: "100%",
  },
  "& .l_purple": {
    top: "14%",
    left: "46%",
    transform: "translateX(-50%) rotate(-2deg)",
  },
  "& .c_purple": {
    top: "-7%",
    left: "58%",
    transform: "translateX(-50%) scale(0.93)",
  },
  "& .r_purple": {
    top: "14%",
    left: "54%",
    transform: "translateX(-50%) rotate(2deg)",
  },
  "&:has(.c_white) .avatar": {
    marginTop: "-30px",
  },
});

const ArtistAvatar = styled("img")({
  height: "76px",
  width: "76px",
  borderRadius: "50%",
  objectFit: "cover",
  loading: "lazy",
});

const TestimonialArtistName = styled(Typography)({
  width: "65%",
  color: "#652784",
  fontSize: "1.125rem",
  fontWeight: 500,
  lineHeight: "21.94px",
  textAlign: "center",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  "@media (max-width: 414px)": {
    fontSize: "1rem",
  },
});

const LightGrayText = styled(Typography)({
  width: "70%",
  color: "#64748B",
  fontSize: "0.75rem",
  fontWeight: 400,
  lineHeight: "14.63px",
  textAlign: "center",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  marginTop: "4px",
  "@media (max-width: 414px)": {
    fontSize: "0.7rem",
  },
});

const FeedbackText = styled(Typography)({
  width: "70%",
  color: "#64748B",
  fontSize: "0.75rem",
  fontWeight: 400,
  lineHeight: "14.63px",
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginTop: "4px",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 6,
  height: "calc(14.63px * 6)",
  "@media (max-width: 414px)": {
    fontSize: "0.7rem",
  },
});

const CustomSwiperSlide = styled(SwiperSlide)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
});

const COVER_FLOW_EFFECT_CONFIG = {
  rotate: 0,
  stretch: 0,
  depth: 180,
  modifier: 1.2,
  slideShadows: false,
};

const PAGINATION_CONFIG = {
  clickable: true,
  el: ".custom-swiper-pagination",
  renderBullet: (_index: number, className: string) =>
    `<span class="${className} custom_bullet"></span>`,
};

interface Testimonial {
  id: string;
  type: string;
  attributes: {
    name: string;
    designation: string;
    content: string;
    created_at: string;
    updated_at: string;
    profile_image: string;
  };
}

interface Props {
  testimonials: Testimonial[];
  getBackgroundCards: (index: number) => React.ReactNode;
}

export class LandingTestimonialsCarousel extends Component<Props> {
  render() {
    const { testimonials, getBackgroundCards } = this.props;
    return (
      <>
          <div className="custom-swiper-pagination"></div>
          <Swiper
            loop
            speed={800}
            centeredSlides
            spaceBetween={80}
            effect="coverflow"
            slidesPerView="auto"
            pagination={PAGINATION_CONFIG}
            coverflowEffect={COVER_FLOW_EFFECT_CONFIG}
            breakpoints={TESTIMONIALS_SLIDER_BREAKPOINTS}
            modules={[Pagination, EffectCoverflow]}
          >
            {testimonials.map((testimonial: Testimonial, index: number) => {
              const {
                id,
                attributes: { profile_image, name, designation, content },
              } = testimonial;
              return (
                <CustomSwiperSlide key={id} data-test-id="testimonial-slide">
                  <TestimonialContentWrapper>
                    <ArtistAvatar
                      src={profile_image}
                      alt={name}
                      className="Avatar"
                    />
                    <TestimonialArtistName variant="h5" title={name}>
                      {name}
                    </TestimonialArtistName>
                    <LightGrayText variant="h6" title={designation}>
                      {designation}
                    </LightGrayText>
                    <img src={BlockQuote} alt="Quote" />
                    <FeedbackText
                      title={content}
                      sx={{ mt: { xs: "2px", sm: "5px", md: "10px" } }}
                    >
                      {content}
                    </FeedbackText>
                    {getBackgroundCards(index)}
                  </TestimonialContentWrapper>
                </CustomSwiperSlide>
              );
            })}
          </Swiper>
      </>
    );
  }
}

export default LandingTestimonialsCarousel;
// Customizable Area End
